import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { NotificationBell } from '@/components/notifications';
import { HelpPanel } from '@/components/onboarding';
import { SearchBar } from '@/components/search';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { hasAdminAccess } from '@/hooks/use-admin-access';
import { cn } from '@/lib/utils';
import { isBctStaff, useAuthStore } from '@/stores/auth';
import { LanguageSwitcher } from './LanguageSwitcher';
import { MobileNav } from './MobileNav';
import { ThemeToggle } from './ThemeToggle';

/**
 * Get the current context for the help panel based on the route
 */
function getCurrentContext(pathname: string): string {
  if (pathname === '/' || pathname === '/dashboard') return 'dashboard';
  if (pathname.startsWith('/issues')) return 'issues';
  if (pathname.startsWith('/tests') || pathname.startsWith('/test-')) return 'test-execution';
  if (pathname.startsWith('/documents')) return 'documents';
  if (pathname.startsWith('/projects')) return 'projects';
  if (pathname.startsWith('/admin')) return 'admin';
  return 'dashboard';
}

interface HeaderProps {
  tenantSwitcher?: React.ReactNode;
}

// Pure utility functions - moved outside component to prevent recreation on render
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getRoleBadgeColor(role: string): string {
  if (role.startsWith('BCT_')) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  }
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
}

export function Header({ tenantSwitcher }: HeaderProps) {
  const { user, clearAuth } = useAuthStore();
  const { t } = useTranslation('common');
  const location = useLocation();
  const currentContext = getCurrentContext(location.pathname);

  // Performance: useCallback to prevent function recreation on render
  const handleLogout = useCallback(async () => {
    try {
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Continue with logout even if request fails
    }
    clearAuth();
    window.location.href = '/login';
  }, [clearAuth]);

  // Check if user has admin panel access (BCT_ADMIN or BCT_PM)
  const showAdminPanel = user ? hasAdminAccess(user.role) : false;

  // Check if this is a first-login user (never selected a language)
  const isFirstLogin = user && user.preferredLanguage === null;
  // Show language switcher for: unauthenticated users OR first-login users
  const showLanguageSwitcher = !user || isFirstLogin;

  return (
    <header
      className="fixed left-0 right-0 top-0 z-40 flex h-16 items-center justify-between border-b border-primary/20 dark:border-neutral-800 bg-header-bg dark:bg-neutral-950 text-primary-foreground px-4"
      style={{ fontFamily: "'Funnel Display', sans-serif" }}
    >
      <div className="flex items-center gap-2">
        <MobileNav />
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/images/logo-corsa.png" alt="PreGo" className="h-10 w-auto" />
          <span className="text-xl font-bold tracking-tight">PreGo!</span>
        </Link>
      </div>

      {/* Center section - Search bar */}
      <div className="hidden flex-1 justify-center px-4 md:flex lg:max-w-xl">
        {user && <SearchBar />}
      </div>

      <div className="flex items-center gap-2">
        {user && isBctStaff(user.role) && tenantSwitcher}

        {/* Globe shows for unauthenticated users and first-login users */}
        {showLanguageSwitcher && <LanguageSwitcher isFirstLogin={!!isFirstLogin} />}
        <ThemeToggle />

        {user && <NotificationBell />}
        {user && <HelpPanel currentContext={currentContext} />}

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-white/20">
                <Avatar className="h-9 w-9 border-2 border-white/30">
                  <AvatarImage src={user.profilePictureUrl ?? undefined} alt={user.name} />
                  <AvatarFallback className="bg-white/20 text-primary-foreground font-medium">
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user.name}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                  <span
                    className={cn(
                      'mt-1.5 inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium',
                      getRoleBadgeColor(user.role),
                    )}
                  >
                    {user.role.replace('_', ' ')}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex w-full cursor-pointer items-center">
                  <Icon name="settings" className="mr-2 h-4 w-4" />
                  {t('nav.settings')}
                </Link>
              </DropdownMenuItem>
              {showAdminPanel && (
                <DropdownMenuItem asChild data-testid="admin-panel-link">
                  <Link to="/admin/panel" className="flex w-full cursor-pointer items-center">
                    <Icon name="admin_panel_settings" className="mr-2 h-4 w-4" />
                    {t('header.adminPanel')}
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <Icon name="logout" className="mr-2 h-4 w-4" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
